import {
    getFilterOptionsFromClassifier,
    getSortedFilterOptions,
    mapToFilterOptions,
} from 'components/tables/list-table/filterUtil.ts';
import { FilterConfig } from 'components/tables/list-table/ListTableTypes';
import { useEffect, useMemo, useState } from 'react';
import { useLazyGetMinContactsListWithFiltersQuery } from 'store/api/contacts.ts';
import { useLazyGetAllEmployeesListWithFiltersQuery } from 'store/api/employees';
import { useLazyGetMinGroupsListWithFiltersQuery } from 'store/api/groups.ts';
import { useGetInvoiceSpecTemplatesQuery, useGetInvoiceTemplatesQuery } from 'store/api/invoices';
import { useLazyGetMattersListQuery } from 'store/api/matters';
import { useLazyGetMinOfficesWithFiltersQuery } from 'store/api/offices.ts';
import {
    useLazyGetPracticeAreasWithFiltersQuery,
    useLazyGetSecondLevelPracticeAreasUngroupedQuery,
} from 'store/api/practice-areas';
import { useLazyGetProjectsWithFiltersQuery } from 'store/api/projects';
import { useLazyGetMinTeamsListWithFiltersQuery } from 'store/api/teams';
import { useGetCurrentTenantQuery } from 'store/api/tenants';
import { ClassifierData } from 'types/classifiers.ts';
import { RelationshipTypeCode } from 'types/contacts.ts';
import { ContractStatusType } from 'types/contracts.ts';
import { FeatureFlag } from 'types/features.ts';
import { MatterStateType, PricingLimitationType } from 'types/matters';
import { PracticeAreaType } from 'types/practice-areas';
import { ReferralTypeCode } from 'types/referrals.ts';
import { TeamType } from 'types/teams';
import { t } from 'utils/getTranslations';
import { renderMatterIndicators } from 'utils/matterOptionRenderer';
import { officeInactiveSuffix } from 'utils/officeOptionRenderer';
import { useHasPermissions } from 'utils/permissionUtil.ts';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags.ts';
import { useAdministratorField } from 'hooks/useAdministratorField';

/** The classifiers the cases list reads, its filters included. */
export const MATTER_LIST_CLASSIFIERS = [
    'ARCHIVING_PROPERTIES_TYPE',
    'BUSINESS_AREA_TYPE',
    'CONTRACT_RELATED_KEYWORD_TYPE',
    'CONTRACT_STATUS_TYPE',
    'COUNTRY',
    'CURRENCY',
    'EXPENSE_TYPE',
    'INVOICING_INTERVAL_TYPE',
    'MATTER_STATE_TYPE',
    'MATTER_TYPE',
    'PRICING_LIMITATION_TYPE',
    'REFERRAL_TYPE',
    'INVOICE_STATUS_TYPE',
    'REFERRAL_OTHER_SOURCE_TYPE',
    'MATTER_BLOCKING_POLICY_TYPE',
];

export const filterMatterStateTypes = [
    MatterStateType.IN_PROGRESS,
    MatterStateType.OFFER_PENDING,
    MatterStateType.COMPLETED,
    MatterStateType.OFFER_WON,
    MatterStateType.OFFER_LOST,
];

const filterMatterPricingLimitationTypes = [
    PricingLimitationType.HOURLY_CAPPED,
    PricingLimitationType.FIXED_ONETIME,
    PricingLimitationType.HOURLY_UNLIMITED,
    PricingLimitationType.FIXED_RECURRING,
    // PricingLimitationType.SUCCESS_FEE, // hidden for now
    PricingLimitationType.NON_BILLABLE,
    PricingLimitationType.PRO_BONO,
];

const filterReferredBySourceTyoes = [ReferralTypeCode.CONTACT, ReferralTypeCode.EMPLOYEE, ReferralTypeCode.OTHER];

const filterContractStatusTypes = [ContractStatusType.VALID, ContractStatusType.TERMINATED, ContractStatusType.DRAFT];

// What the new design's condition switch offers on a Cases field; the classic drawer never reads them.
const MULTI_VALUE = { not: true, notComparator: '=notany=', empty: true };
const NULLABLE = { not: true, empty: true };
const NEVER_EMPTY = { not: true, neverEmpty: true };

type Args = {
    classifiers: Record<string, ClassifierData>;
    passiveFilter?: string;
    /** A list that offers no filter of its own: it keeps the search and the filter it always applies. */
    isFilterHidden?: boolean;
};

/**
 * The cases list's filters: its quick filters, the fields of its filter panel, what its search reads and the filter
 * it always applies. One definition for the list and for whatever else filters cases by the same fields.
 */
export function useMatterFilterConfig({ classifiers, passiveFilter = '', isFilterHidden = false }: Args): FilterConfig {
    const isSubPracticeAreaEnabled = useIsFeatureEnabled(FeatureFlag.SUB_PRACTICE_AREA);
    const isCaseAssignmentEnabled = useIsFeatureEnabled(FeatureFlag.CASE_ASSIGNMENT_ENABLED);
    const isOfficialRefNumberEnabled = useIsFeatureEnabled(FeatureFlag.CASE_OFFICIAL_REF_NUMBER_FIELD);
    const { isOn: isAdministratorFieldEnabled, caseLabel: caseAssistantLabel } = useAdministratorField();
    const isCasePaymentCurrencyEnabled = useIsFeatureEnabled(FeatureFlag.CASE_PAYMENT_CURRENCY_FIELD);
    const isInsiderListEnabled = useIsFeatureEnabled(FeatureFlag.MATTER_INSIDER_LIST);
    const isDelegationEnabled = useIsFeatureEnabled(FeatureFlag.DELEGATION);
    // The backend refuses an isInsider filter without READ, as the case detail blanks the flag.
    const canReadInsiderInfo = useHasPermissions(['READ.MATTER.INSIDER_LIST']);

    const { data: currentTenant } = useGetCurrentTenantQuery();
    const baseCurrencyTypeCode = currentTenant?.baseCurrencyTypeCode ?? '';
    // Interval filters render only min/max labels, so the currency has to live on those
    const currencySuffix = baseCurrencyTypeCode ? t('common.currency-suffix', { currency: baseCurrencyTypeCode }) : '';

    const { data: invoiceTemplates } = useGetInvoiceTemplatesQuery();
    const { data: invoiceSpecTemplates } = useGetInvoiceSpecTemplatesQuery();

    const invoiceTemplateOptions = useMemo(
        () =>
            getSortedFilterOptions(
                'name',
                'id',
                (invoiceTemplates ?? []).filter((t) => !t.isCorrection)
            ),
        [invoiceTemplates]
    );
    const invoiceSpecTemplateOptions = useMemo(
        () =>
            getSortedFilterOptions(
                'name',
                'id',
                (invoiceSpecTemplates ?? []).filter((t) => !t.isCorrection)
            ),
        [invoiceSpecTemplates]
    );

    // Check if practice groups exist
    const [checkPracticeGroups] = useLazyGetMinTeamsListWithFiltersQuery();
    const [hasPracticeGroups, setHasPracticeGroups] = useState<boolean | undefined>(undefined);

    useEffect(() => {
        checkPracticeGroups({
            page: 0,
            pageSize: 1,
            filters: `teamTypeCode==${TeamType.PRACTICE_GROUP};isParent==true`,
        }).then((result) => {
            setHasPracticeGroups((result.data?.content?.length ?? 0) > 0);
        });
    }, [checkPracticeGroups]);

    const matterStateTypeOptions = useMemo(
        () => mapToFilterOptions(filterMatterStateTypes, classifiers['MATTER_STATE_TYPE']),
        [classifiers]
    );

    // Firm-defined taxonomy: a tenant that never populated the classifier gets no filter at all.
    const matterTypeOptions = useMemo(() => getFilterOptionsFromClassifier(classifiers['MATTER_TYPE']), [classifiers]);

    const feeArragementTypeOptions = useMemo(
        () => mapToFilterOptions(filterMatterPricingLimitationTypes, classifiers['PRICING_LIMITATION_TYPE']),
        [classifiers]
    );

    const referredBySourceTypeOptions = useMemo(
        () => mapToFilterOptions(filterReferredBySourceTyoes, classifiers['REFERRAL_TYPE']),
        [classifiers]
    );

    const contractStatusTypeOptions = useMemo(
        () => mapToFilterOptions(filterContractStatusTypes, classifiers['CONTRACT_STATUS_TYPE']),
        [classifiers]
    );

    const filterConfig: FilterConfig = useMemo(
        () => ({
            quickFilters: !isFilterHidden
                ? [
                      {
                          selector: 'id',
                          comperator: '=ismycase=',
                          filters: [
                              {
                                  tooltip: 'matters.tooltips.my-cases',
                                  label: t('matters.my-cases'),
                                  argument: 'true',
                              },
                          ],
                      },
                      {
                          selector: 'id',
                          comperator: '=ismyteamcase=',
                          filters: [
                              {
                                  tooltip: 'matters.tooltips.my-team-cases',
                                  label: t('matters.my-team-cases'),
                                  argument: 'true',
                              },
                          ],
                      },
                      ...(isDelegationEnabled
                          ? [
                                {
                                    selector: 'id',
                                    comperator: '=iscoveredbyme=',
                                    filters: [
                                        {
                                            tooltip: 'matters.tooltips.cases-i-cover',
                                            label: t('matters.cases-i-cover'),
                                            argument: 'true',
                                        },
                                    ],
                                },
                            ]
                          : []),
                      {
                          selector: 'matterStateTypeCode',
                          chipLabel: 'filters.status',
                          multiple: true,
                          filters: matterStateTypeOptions,
                      },
                  ]
                : [],
            filters: !isFilterHidden
                ? [
                      {
                          type: 'dynamicmultiselect',
                          subType: 'contact',
                          label: t('matters.filter.contact-name'),
                          selector: 'clientParty.id',
                          conditions: NEVER_EMPTY,
                          group: 'matters.client',
                          tooltip: 'matters.tooltips.list-displayed-for-relationship-type-selections',
                          paginated: {
                              fetch: useLazyGetMinContactsListWithFiltersQuery,
                              searchField: ['name', 'partyNumber'],
                              filters: `relationshipTypeCode=in=(${[RelationshipTypeCode.CLIENT, RelationshipTypeCode.LEAD].join(',')})`,
                              getOptionIdentifier: 'id',
                              getOptionLabel: 'name',
                          },
                      },
                      {
                          type: 'dynamicmultiselect',
                          label: t('contacts.about.relationship-partner'),
                          selector: 'clientParty.partners.employee.id',
                          conditions: MULTI_VALUE,
                          group: 'matters.client',
                          paginated: {
                              fetch: useLazyGetAllEmployeesListWithFiltersQuery,
                              searchField: 'fullName',
                              getOptionIdentifier: 'id',
                              getOptionLabel: 'name',
                          },
                      },
                      {
                          type: 'dynamicmultiselect',
                          label: t('contacts.about.relationship-manager'),
                          selector: 'clientParty.managers.employee.id',
                          conditions: MULTI_VALUE,
                          group: 'matters.client',
                          paginated: {
                              fetch: useLazyGetAllEmployeesListWithFiltersQuery,
                              searchField: 'fullName',
                              getOptionIdentifier: 'id',
                              getOptionLabel: 'name',
                          },
                      },
                      ...(isAdministratorFieldEnabled
                          ? [
                                {
                                    type: 'dynamicmultiselect' as const,
                                    label: caseAssistantLabel,
                                    selector: 'administrators.employee.id',
                                    conditions: MULTI_VALUE,
                                    group: 'matters.client',
                                    paginated: {
                                        fetch: useLazyGetAllEmployeesListWithFiltersQuery,
                                        searchField: 'fullName',
                                        getOptionIdentifier: 'id' as const,
                                        getOptionLabel: 'name' as const,
                                    },
                                },
                            ]
                          : []),
                      {
                          type: 'multiselect',
                          label: t('matters.filter.contact-business-area'),
                          selector: 'clientParty.businessAreas.businessAreaCode',
                          conditions: MULTI_VALUE,
                          group: 'matters.client',
                          options: classifiers
                              ? getFilterOptionsFromClassifier(classifiers?.['BUSINESS_AREA_TYPE'])
                              : [],
                      },
                      {
                          type: 'dynamicmultiselect',
                          group: 'matters.client',
                          label: t('contacts.filter.sector-group'),
                          selector: 'clientParty.id',
                          comperator: '=hasbusinessareasinsectorgroup=',
                          conditions: { not: true },
                          tooltip: 'matters.tooltips.filter-by-sector-group',
                          paginated: {
                              fetch: useLazyGetMinTeamsListWithFiltersQuery,
                              searchField: 'name',
                              filters: `teamTypeCode==${TeamType.SECTOR_GROUP};isParent==true`,
                              getOptionIdentifier: 'id',
                              getOptionLabel: 'name',
                          },
                      },
                      {
                          type: 'multiselect',
                          label: t('matters.filter.contact-country'),
                          selector: 'clientParty.addresses.countryCode',
                          conditions: MULTI_VALUE,
                          group: 'matters.client',
                          options: classifiers ? getFilterOptionsFromClassifier(classifiers?.['COUNTRY']) : [],
                      },
                      // -------- CASE --------
                      {
                          type: 'dynamicmultiselect',
                          label: t('matters.filter.group-name'),
                          selector: 'clientParty.partyGroupParties.partyGroup.id',
                          conditions: MULTI_VALUE,
                          group: 'matters.client',
                          paginated: {
                              fetch: useLazyGetMinGroupsListWithFiltersQuery,
                              searchField: 'groupName',
                              getOptionIdentifier: 'id',
                              getOptionLabel: 'groupName',
                          },
                      },
                      {
                          type: 'checkboxgroup',
                          selector: 'id',
                          group: 'matters.case',
                          comperator: '=ismycase=',
                          filters: [
                              {
                                  tooltip: 'matters.tooltips.my-cases',
                                  label: t('matters.my-cases'),
                                  argument: 'true',
                              },
                          ],
                      },
                      {
                          type: 'checkboxgroup',
                          selector: 'id',
                          group: 'matters.case',
                          comperator: '=ismyteamcase=',
                          filters: [
                              {
                                  tooltip: 'matters.tooltips.my-team-cases',
                                  label: t('matters.my-team-cases'),
                                  argument: 'true',
                              },
                          ],
                      },
                      {
                          type: 'checkboxgroup',
                          selector: 'isConfidential',
                          group: 'matters.case',
                          comperator: '==',
                          no: { selector: 'isConfidential', comparator: '==', argument: 'false' },
                          filters: [
                              {
                                  label: t('matters.is-confidential'),
                                  argument: 'true',
                              },
                          ],
                      },
                      {
                          type: 'checkboxgroup',
                          selector: 'isInsider',
                          group: 'matters.case',
                          comperator: '==',
                          no: { selector: 'isInsider', comparator: '==', argument: 'false' },
                          filters: [
                              {
                                  label: t('matters.is-insider'),
                                  argument: 'true',
                              },
                          ],
                          getShouldHide: () => !(isInsiderListEnabled && canReadInsiderInfo),
                      },
                      {
                          type: 'checkboxgroup',
                          selector: 'id',
                          group: 'matters.case',
                          comperator: '=hasamlapplicablepracticearea=',
                          no: { selector: 'id', comparator: '=nothasamlapplicablepracticearea=', argument: 'true' },
                          yesNoCopy: {
                              help: 'matters.filter.aml-applicable-cases-help',
                              yes: 'matters.filter.aml-applicable-cases-yes',
                              no: 'matters.filter.aml-applicable-cases-no',
                          },
                          filters: [
                              {
                                  label: t('matters.filter.aml-applicable-cases'),
                                  argument: 'true',
                                  tooltip: 'matters.tooltips.filter-aml-applicable-cases',
                              },
                          ],
                      },
                      {
                          type: 'dynamicmultiselect',
                          selector: 'id',
                          label: t('matters.filter.case-name'),
                          conditions: NEVER_EMPTY,
                          group: 'matters.case',
                          getOptionStartAdornment: renderMatterIndicators,
                          paginated: {
                              fetch: useLazyGetMattersListQuery,
                              searchField: 'fullName',
                              getOptionIdentifier: 'id',
                              getOptionLabel: 'fullName',
                          },
                      },
                      {
                          type: 'dynamicmultiselect',
                          label: t('matters.filter.project-name'),
                          selector: 'projectMatters.project.id',
                          conditions: MULTI_VALUE,
                          group: 'matters.case',
                          tooltip: 'matters.tooltips.filter-cases-by-project',
                          paginated: {
                              fetch: useLazyGetProjectsWithFiltersQuery,
                              searchField: 'projectName',
                              getOptionIdentifier: 'id',
                              getOptionLabel: 'projectName',
                          },
                      },
                      {
                          type: 'dynamicmultiselect',
                          label: t('matters.filter.supervising-partner'),
                          selector: 'supervisedByEmployee.id',
                          conditions: NULLABLE,
                          group: 'matters.case',
                          paginated: {
                              fetch: useLazyGetAllEmployeesListWithFiltersQuery,
                              searchField: 'fullName',
                              getOptionIdentifier: 'id',
                              getOptionLabel: 'name',
                          },
                      },
                      {
                          type: 'dynamicmultiselect',
                          label: t('matters.responsible-lawyer'),
                          selector: 'responsibleEmployee.id',
                          conditions: NULLABLE,
                          group: 'matters.case',
                          paginated: {
                              fetch: useLazyGetAllEmployeesListWithFiltersQuery,
                              searchField: 'fullName',
                              getOptionIdentifier: 'id',
                              getOptionLabel: 'name',
                          },
                      },
                      {
                          type: 'dynamicmultiselect',
                          label: t('matters.handled-by'),
                          selector: 'employees.employee.id',
                          conditions: MULTI_VALUE,
                          group: 'matters.case',
                          paginated: {
                              fetch: useLazyGetAllEmployeesListWithFiltersQuery,
                              searchField: 'fullName',
                              getOptionIdentifier: 'id',
                              getOptionLabel: 'name',
                          },
                      },
                      {
                          type: 'dynamicmultiselect',
                          label: t('matters.filter.practice-areas'),
                          selector: 'practiceAreas.practiceArea.id',
                          conditions: MULTI_VALUE,
                          group: 'matters.case',
                          tooltip: 'matters.tooltips.filter-cases-practice-area',
                          getOptionStartAdornment: (option) => option.taxonomyId && `[${option.taxonomyId}]`,
                          paginated: {
                              fetch: useLazyGetPracticeAreasWithFiltersQuery,
                              searchField: 'name',
                              getOptionIdentifier: 'id',
                              getOptionLabel: 'name',
                              filters: `practiceAreaTypeCode==${PracticeAreaType.PRACTICE_AREA}`,
                          },
                      },
                      {
                          type: 'dynamicmultiselect',
                          label: t('matters.filter.sub-practice-areas'),
                          selector: 'practiceAreas.practiceArea.id',
                          comperator: '=issecondlevelpracticearea=',
                          group: 'matters.case',
                          tooltip: 'matters.tooltips.filter-cases-sub-practice-area',
                          getOptionStartAdornment: (option) => option.taxonomyId && `[${option.taxonomyId}]`,
                          isFilterHidden: !isSubPracticeAreaEnabled,
                          paginated: {
                              fetch: useLazyGetSecondLevelPracticeAreasUngroupedQuery,
                              searchField: 'name',
                              getOptionIdentifier: 'id',
                              getOptionLabel: 'name',
                          },
                      },
                      {
                          type: 'dynamicmultiselect',
                          label: t('matters.filter.practice-group'),
                          selector: 'practiceAreas.practiceArea.id',
                          comperator: '=ispracticeareaavailableforpracticegroup=',
                          group: 'matters.case',
                          tooltip: 'matters.tooltips.filter-cases-by-practice-group',
                          isFilterHidden: hasPracticeGroups === false,
                          paginated: {
                              fetch: useLazyGetMinTeamsListWithFiltersQuery,
                              searchField: 'name',
                              filters: `teamTypeCode==${TeamType.PRACTICE_GROUP};isParent==true`,
                              getOptionIdentifier: 'id',
                              getOptionLabel: 'name',
                          },
                      },
                      {
                          type: 'multiselect',
                          label: t('matters.filter.case-type'),
                          selector: 'matterTypeCode',
                          conditions: NULLABLE,
                          group: 'matters.case',
                          options: matterTypeOptions,
                          getShouldHide: () => matterTypeOptions.length === 0,
                      },
                      {
                          type: 'multiselect',
                          label: t('matters.filter.case-status'),
                          selector: 'matterStateTypeCode',
                          conditions: NEVER_EMPTY,
                          group: 'matters.case',
                          options: matterStateTypeOptions,
                      },
                      {
                          type: 'text',
                          label: t('matters.filter.official-reference-number'),
                          selector: 'officialReference',
                          conditions: { empty: true },
                          group: 'matters.case',
                          getShouldHide: () => !isOfficialRefNumberEnabled,
                      },
                      {
                          type: 'dynamicmultiselect',
                          label: t('matters.filter.invoiced-by-office'),
                          selector: 'invoicedByOffice.id',
                          conditions: NULLABLE,
                          group: 'matters.case',
                          paginated: {
                              fetch: useLazyGetMinOfficesWithFiltersQuery,
                              searchField: 'name',
                              getOptionIdentifier: 'id',
                              getOptionLabel: 'name',
                          },
                          getOptionEndAdornment: officeInactiveSuffix,
                      },
                      {
                          type: 'multiselect',
                          label: t('matters.filter.invoice-template'),
                          selector: 'invoiceTemplate.id',
                          conditions: { not: true },
                          group: 'matters.case',
                          options: invoiceTemplateOptions,
                          nullOption: { label: t('matters.filter.no-invoice-template') },
                      },
                      {
                          type: 'multiselect',
                          label: t('matters.filter.invoice-spec-template'),
                          selector: 'invoiceSpecTemplate.id',
                          conditions: { not: true },
                          group: 'matters.case',
                          options: invoiceSpecTemplateOptions,
                          nullOption: { label: t('matters.filter.no-invoice-spec-template') },
                      },
                      {
                          type: 'checkboxgroup',
                          label: t('matters.filter.is-spec-included-in-invoice'),
                          selector: 'isSpecIncludedInInvoice',
                          group: 'matters.case',
                          comperator: '==',
                          multiple: false,
                          displayDirection: 'row',
                          filters: [
                              {
                                  label: t('yes'),
                                  argument: 'true',
                                  rsqlOverride: '(isSpecIncludedInInvoice==true,isSpecIncludedInInvoice=isnull=true)',
                              },
                              {
                                  label: t('no'),
                                  argument: 'false',
                              },
                          ],
                      },
                      {
                          type: 'dynamicmultiselect',
                          label: t('matters.filter.handled-by-office'),
                          selector: 'handledByOffice.id',
                          conditions: NULLABLE,
                          group: 'matters.case',
                          paginated: {
                              fetch: useLazyGetMinOfficesWithFiltersQuery,
                              searchField: 'name',
                              getOptionIdentifier: 'id',
                              getOptionLabel: 'name',
                          },
                          getOptionEndAdornment: officeInactiveSuffix,
                      },
                      {
                          type: 'multiselect',
                          selector: 'pricingLimitationTypeCode',
                          conditions: NEVER_EMPTY,
                          label: t('matters.filter.fee-arrangement'),
                          group: 'matters.case',
                          options: feeArragementTypeOptions,
                      },
                      {
                          type: 'multiselect',
                          label: t('matters.filter.case-currency'),
                          group: 'matters.case',
                          selector: 'pricelistCurrencyTypeCode',
                          conditions: NEVER_EMPTY,
                          tooltip: 'matters.tooltips.filters-cases-with-selected-currency-connections',
                          options: classifiers ? getFilterOptionsFromClassifier(classifiers?.['CURRENCY']) : [],
                      },
                      {
                          type: 'multiselect',
                          label: t('matters.filter.payment-currency'),
                          group: 'matters.case',
                          selector: 'paymentCurrencyTypeCode',
                          conditions: NULLABLE,
                          options: classifiers ? getFilterOptionsFromClassifier(classifiers?.['CURRENCY']) : [],
                          getShouldHide: () => !isCasePaymentCurrencyEnabled,
                      },
                      {
                          type: 'multiselect',
                          label: t('matters.filter.invoicing-interval'),
                          group: 'matters.case',
                          selector: 'invoicingIntervalTypeCode',
                          conditions: NULLABLE,
                          options: classifiers
                              ? getFilterOptionsFromClassifier(classifiers?.['INVOICING_INTERVAL_TYPE'])
                              : [],
                      },
                      {
                          type: 'checkboxgroup',
                          label: t('matters.filter.referred-by-source'),
                          group: 'matters.case',
                          selector: 'referrals.referralTypeCode',
                          multiple: true,
                          filters: referredBySourceTypeOptions,
                      },
                      {
                          type: 'dynamicmultiselect',
                          subType: 'contact',
                          label: t('matters.filter.referred-by-name'),
                          group: 'matters.case',
                          selector: 'referrals.id',
                          comperator: '=isreferredparty=',
                          paginated: {
                              fetch: useLazyGetMinContactsListWithFiltersQuery,
                              searchField: ['name', 'partyNumber'],
                              getOptionIdentifier: 'id',
                              getOptionLabel: 'name',
                          },
                      },
                      {
                          type: 'dynamicmultiselect',
                          label: t('matters.filter.referred-by-colleague'),
                          group: 'matters.case',
                          selector: 'referrals.employee.id',
                          conditions: MULTI_VALUE,
                          paginated: {
                              fetch: useLazyGetAllEmployeesListWithFiltersQuery,
                              searchField: 'fullName',
                              getOptionIdentifier: 'id',
                              getOptionLabel: 'name',
                          },
                      },
                      {
                          type: 'dynamicmultiselect',
                          subType: 'contact',
                          label: t('matters.filter.payer'),
                          group: 'matters.case',
                          selector: 'payers.party.id',
                          conditions: MULTI_VALUE,
                          paginated: {
                              fetch: useLazyGetMinContactsListWithFiltersQuery,
                              searchField: ['name', 'partyNumber'],
                              getOptionIdentifier: 'id',
                              getOptionLabel: 'name',
                          },
                      },
                      {
                          type: 'multiselect',
                          label: t('matters.filter.archiving-properties'),
                          group: 'matters.case',
                          selector: 'archivingPropertiesTypeCode',
                          conditions: NULLABLE,
                          options: classifiers
                              ? getFilterOptionsFromClassifier(classifiers?.['ARCHIVING_PROPERTIES_TYPE'])
                              : [],
                      },
                      {
                          type: 'daterange',
                          label: t('matters.opening-date'),
                          shouldDisplayQuickPick: true,
                          group: 'matters.case',
                          selector: 'openedDate',
                      },
                      {
                          type: 'daterange',
                          label: t('matters.closing-date'),
                          shouldDisplayQuickPick: true,
                          group: 'matters.case',
                          selector: 'closedDate',
                          conditions: { empty: true },
                      },
                      {
                          type: 'daterange',
                          label: t('matters.last-timecard-date'),
                          tooltip: 'matters.tooltips.last-timecard-date',
                          group: 'matters.case',
                          shouldDisplayQuickPick: true,
                          selector: 'latestTimecardDate',
                          conditions: { empty: true },
                      },
                      {
                          type: 'interval',
                          label: t('matters.filter.billable-hours'),
                          group: 'matters.case',
                          selector: 'id',
                          comperator: '=billablehoursrange=',
                          minLabel: t('matters.filter.billable-hours-from'),
                          maxLabel: t('matters.filter.billable-hours-to'),
                      },
                      {
                          type: 'interval',
                          label: t('matters.filter.total-case-revenue'),
                          group: 'matters.case',
                          selector: 'id',
                          comperator: '=caserevenuerange=',
                          minLabel: t('matters.filter.total-case-revenue-from') + currencySuffix,
                          maxLabel: t('matters.filter.total-case-revenue-to') + currencySuffix,
                      },
                      // -------- CONTRACT --------
                      // Every selector here hangs off the case's service-agreement contract, so a case
                      // without one is excluded by all of them except the "without" checkbox.
                      {
                          type: 'offset-date-time-range',
                          label: t('contracts.filter.signed-on'),
                          selector: 'serviceAgreementContract.contractSignedAt',
                          comperator: '=daterange=',
                          group: 'matters.contract',
                          shouldDisplayQuickPick: true,
                      },
                      {
                          type: 'multiselect',
                          label: t('contracts.filter.contract-keywords'),
                          selector: 'serviceAgreementContract.contractRelatedKeywords.contractRelatedKeywordCode',
                          conditions: MULTI_VALUE,
                          group: 'matters.contract',
                          options: classifiers
                              ? getFilterOptionsFromClassifier(classifiers?.['CONTRACT_RELATED_KEYWORD_TYPE'])
                              : [],
                      },
                      {
                          type: 'multiselect',
                          label: t('contracts.status'),
                          selector: 'serviceAgreementContract.contractStatusTypeCode',
                          conditions: MULTI_VALUE,
                          group: 'matters.contract',
                          options: contractStatusTypeOptions,
                      },
                      {
                          type: 'checkboxgroup',
                          selector: 'serviceAgreementContract.id',
                          comperator: '=isnull=',
                          no: { selector: 'serviceAgreementContract.id', comparator: '=isnotnull=', argument: 'true' },
                          group: 'matters.contract',
                          filters: [
                              {
                                  label: t('matters.filter.cases-without-service-agreement'),
                                  argument: 'true',
                              },
                          ],
                      },
                      // -------- OTHER --------
                      {
                          type: 'checkboxgroup',
                          selector: 'id',
                          comperator: '=hasmatterassignments=',
                          no: { selector: 'id', comparator: '=nothasmatterassignments=', argument: 'true' },
                          group: 'matters.other.title',
                          isFilterHidden: !isCaseAssignmentEnabled,
                          filters: [
                              {
                                  label: t('matters.filter.case-has-assignments'),
                                  argument: 'true',
                                  tooltip: 'matters.tooltips.filter-cases-with-assignments',
                              },
                          ],
                      },
                      {
                          type: 'checkboxgroup',
                          selector: 'id',
                          comperator: '=hasidlecases=',
                          no: { selector: 'id', comparator: '=nothasidlecases=', argument: 'true' },
                          group: 'matters.other.title',
                          filters: [
                              {
                                  label: t('matters.filter.ready-to-close'),
                                  argument: 'true',
                                  tooltip: 'matters.tooltips.filters-cases-ready-to-close',
                              },
                          ],
                      },
                      {
                          type: 'checkboxgroup',
                          selector: 'id',
                          comperator: '=hasuninvoicedtimecards=',
                          no: { selector: 'id', comparator: '=nothasuninvoicedtimecards=', argument: 'true' },
                          group: 'matters.other.title',
                          filters: [
                              {
                                  label: t('matters.filter.cases-with-uninvoiced-timecards'),
                                  argument: 'true',
                                  tooltip:
                                      'matters.tooltips.filters-cases-with-connected-uninvoiced-timecards-not-included-in-sent-invoice',
                              },
                          ],
                      },
                      {
                          type: 'checkboxgroup',
                          selector: 'id',
                          comperator: '=iscompletedandnotarchived=',
                          no: { selector: 'id', comparator: '=notiscompletedandnotarchived=', argument: 'true' },
                          group: 'matters.other.title',
                          filters: [
                              {
                                  label: t('matters.filter.completed-but-not-archived'),
                                  argument: 'true',
                                  tooltip: 'matters.tooltips.filter-completed-but-not-archived-cases',
                              },
                          ],
                      },
                      {
                          type: 'multiselect',
                          label: t('matters.filter.blocking-policy'),
                          selector: 'matterBlockingPolicyTypeCode',
                          conditions: NEVER_EMPTY,
                          group: 'matters.other.title',
                          options: classifiers
                              ? getFilterOptionsFromClassifier(classifiers?.['MATTER_BLOCKING_POLICY_TYPE'])
                              : [],
                      },
                      {
                          type: 'checkboxgroup',
                          selector: 'id',
                          comperator: '=isblockedcase=',
                          no: { selector: 'id', comparator: '=isunblockedcase=', argument: 'true' },
                          group: 'matters.other.title',
                          filters: [
                              {
                                  label: t('matters.filter.is-blocked'),
                                  argument: 'true',
                                  tooltip: 'matters.tooltips.filter-is-blocked',
                              },
                          ],
                      },
                  ]
                : [],
            searchBarSelectors: [
                { selector: 'fullName' },
                { selector: 'description' },
                { selector: 'matterClientReference' },
                { selector: 'invoiceMatterName' },
                { selector: 'invoiceMatterNameLocalLanguage' },
                { selector: 'responsibleEmployee.fullName' },
                { selector: 'responsibleEmployee.localFullName' },
                { selector: 'supervisedByEmployee.fullName' },
                { selector: 'supervisedByEmployee.localFullName' },
                { selector: 'clientParty.name' },
                { selector: 'handledByOffice.name' },
            ],
            passiveFilter: passiveFilter,
        }),
        [
            referredBySourceTypeOptions,
            contractStatusTypeOptions,
            matterStateTypeOptions,
            matterTypeOptions,
            feeArragementTypeOptions,
            classifiers,
            passiveFilter,
            isFilterHidden,
            isSubPracticeAreaEnabled,
            isCaseAssignmentEnabled,
            isOfficialRefNumberEnabled,
            isAdministratorFieldEnabled,
            caseAssistantLabel,
            isCasePaymentCurrencyEnabled,
            isInsiderListEnabled,
            isDelegationEnabled,
            canReadInsiderInfo,
            invoiceTemplateOptions,
            invoiceSpecTemplateOptions,
            hasPracticeGroups,
            currencySuffix,
        ]
    );

    return filterConfig;
}
